import * as React from 'react'
import { useGridApiContext } from '../../hooks/utils/useGridApiContext'
import { GridColumnHeaderSortIcon } from './GridColumnHeaderSortIcon'
import { GridGenericColumnHeaderItem } from './GridGenericColumnHeaderItem'
import { GridColumnHeaderEventLookup } from '../../models/events'
import { ReactTableHeader } from '../../models/gridReactTable'
import { isReactComponent } from '@applift/factor'
import { render } from './GridColumnGroupHeader'
import clsx from 'clsx'
import { useGridRootProps } from '../../hooks/utils/useGridRootProps'
import styles from '../../style'
import { GridColumnHeaderSeparatorProps } from './GridColumnHeaderSeparator'

interface GridColumnHeaderItemProps extends ReactTableHeader {
  colIndex: number
  cellHeight: number
  isLastColumn: boolean
  extendRowFullWidth: boolean
  showColumnRightBorder: boolean
  disableColumnReorder: boolean
  isResizing: boolean
  isDragging: boolean
  hasFocus?: boolean
  tabIndex: 0 | -1
  separatorSide?: GridColumnHeaderSeparatorProps['side']
}

export function GridColumnHeaderItem(props: GridColumnHeaderItemProps) {
  const {
    colIndex,
    cellHeight,
    isLastColumn,
    extendRowFullWidth,
    showColumnRightBorder,
    disableColumnReorder,
    getSize,
    column,
    getContext,
    isDragging,
    isResizing,
    separatorSide,
    tabIndex,
    hasFocus,
  } = props
  const apiRef = useGridApiContext()
  const rootProps = useGridRootProps()
  const headerCellRef = React.useRef<HTMLDivElement>(null)
  const { hasScrollX } = apiRef.current.getRootDimensions() ?? {
    hasScrollX: false,
    hasScrollY: false,
  }
  const removeLastBorderRight = isLastColumn && !hasScrollX
  const showRightBorder = !isLastColumn
    ? showColumnRightBorder
    : !removeLastBorderRight && !extendRowFullWidth
  const header = column.columnDef.header
  const width = getSize()
  const headerAlign = column.columnDef.meta?.headerAlign ?? 'left'
  const disableReorder = column.columnDef.meta?.disableReorder ?? false
  const canSort = column.getCanSort()
  const canResize = column.getCanResize()
  const sortDirection = column.getIsSorted()
  const sortIndex = column.getSortIndex()
  const sortingLength =
    apiRef.current.tableInstance.getState()?.sorting?.length ?? 0

  const firstSortDir = column.getFirstSortDir()
  const isDraggable = React.useMemo(
    () =>
      !rootProps.disableColumnReorder &&
      !disableColumnReorder &&
      !disableReorder,
    [rootProps.disableColumnReorder, disableColumnReorder, disableReorder]
  )

  let label: any = column.id
  let headerComponent: React.ReactNode
  if (isReactComponent(header)) {
    headerComponent = render(header, {
      ...getContext(),
      others: {
        tabIndex,
        hasFocus,
        isLastColumn,
        removeLastBorderRight,
        showRightBorder,
        apiRef: apiRef.current,
      },
    })
  } else {
    label = header
  }

  const columnTitleIconButtons = (
    <React.Fragment>
      {/* Todo filter icons */}

      {canSort && (
        <GridColumnHeaderSortIcon
          direction={sortDirection}
          index={sortIndex}
          firstSortDir={firstSortDir}
          showBadge={rootProps.showSortingIndex && sortingLength > 1}
        />
      )}
    </React.Fragment>
  )

  const classes = React.useMemo(() => {
    return {
      root: clsx(
        [styles.columnHeader, rootProps.classes?.columnHeader],
        headerAlign === 'left' && [
          styles['columnHeader--alignLeft'],
          rootProps.classes?.['columnHeader--alignLeft'],
        ],
        headerAlign === 'center' && [
          styles['columnHeader--alignCenter'],
          rootProps.classes?.['columnHeader--alignCenter'],
        ],
        headerAlign === 'right' && [
          styles['columnHeader--alignRight'],
          rootProps.classes?.['columnHeader--alignRight'],
        ],
        showRightBorder && [styles.withBorder, rootProps.classes?.withBorder],
        canSort && [
          styles['columnHeader--sortable'],
          rootProps.classes?.['columnHeader--sortable'],
        ],
        sortDirection && [
          styles['columnHeader--sorted'],
          rootProps.classes?.['columnHeader--sorted'],
        ],
        isDragging && [
          styles['columnHeader--moving'],
          rootProps.classes?.['columnHeader--moving'],
        ]
      ),
      draggableContainer: clsx([
        styles.columnHeaderDraggableContainer,
        rootProps.classes?.columnHeaderDraggableContainer,
      ]),
      titleContainer: clsx([
        styles.columnHeaderTitleContainer,
        rootProps.classes?.columnHeaderTitleContainer,
      ]),
      titleContainerContent: clsx([
        styles.columnHeaderTitleContainerContent,
        rootProps.classes?.columnHeaderTitleContainerContent,
      ]),
    }
  }, [
    headerAlign,
    showRightBorder,
    canSort,
    isDragging,
    sortDirection,
    rootProps.classes,
  ])

  const publish = React.useCallback(
    (eventName: keyof GridColumnHeaderEventLookup) =>
      (event: React.SyntheticEvent) => {
        // Ignore portal
        // See https://github.com/mui/mui-x/issues/1721
        if (!event.currentTarget.contains(event.target as Element)) {
          return
        }
        const _column = apiRef.current.tableInstance.getColumn(column.id)
        if (!_column) {
          return
        }
        apiRef.current.publishEvent(
          eventName,
          {
            field: column.id,
            maxWidth: _column.columnDef.maxSize,
            minWidth: _column.columnDef.minSize,
            width: _column.getSize(),
            canResize: _column.getCanResize(),
            computedWidth: _column.getSize(),
            disableReorder: _column.columnDef.meta?.disableReorder,
          },
          event as any
        )
      },
    [apiRef, column.id]
  )

  const mouseEventsHandlers = React.useMemo(
    () => ({
      onClick: publish('columnHeaderClick'),
      onDoubleClick: publish('columnHeaderDoubleClick'),
      onKeyDown: publish('columnHeaderKeyDown'),
      onFocus: publish('columnHeaderFocus'),
      onBlur: publish('columnHeaderBlur'),
    }),
    [publish]
  )

  const draggableEventHandlers = React.useMemo(
    () =>
      isDraggable
        ? {
            onDragStart: publish('columnHeaderDragStart'),
            onDragEnter: publish('columnHeaderDragEnter'),
            onDragOver: publish('columnHeaderDragOver'),
            onDragEnd: publish('columnHeaderDragEnd'),
          }
        : {},
    [isDraggable, publish]
  )

  const columnHeaderSeparatorProps = React.useMemo(
    () => ({ onMouseDown: publish('columnSeparatorMouseDown') }),
    [publish]
  )

  return (
    <GridGenericColumnHeaderItem
      ref={headerCellRef}
      classes={classes}
      colIndex={colIndex}
      height={cellHeight}
      isResizing={isResizing}
      sortDirection={sortDirection}
      isDraggable={isDraggable}
      headerComponent={headerComponent}
      separatorSide={separatorSide}
      width={width}
      columnTitleIconButtons={columnTitleIconButtons}
      label={label}
      tabIndex={tabIndex}
      hasFocus={hasFocus}
      description={column.columnDef.meta?.description}
      descriptionProps={column.columnDef.meta?.descriptionProps}
      resizable={canResize}
      data-field={column.id}
      draggableContainerProps={draggableEventHandlers}
      columnHeaderSeparatorProps={columnHeaderSeparatorProps}
      {...mouseEventsHandlers}
    />
  )
}
